import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Accordion from '../../../shared/Accordion';
import RowActionsMenu from '../../../shared/RowActionsMenu';
import SaveButton from '../../../shared/SaveButton';
import CancelButton from '../../../shared/CancelButton';
import { Pill } from '../../../shared/Pill';
import { AlertTriangle } from 'lucide-react';
import { useScreenAccess, usePermissions } from '../../../shared/PermissionsContext';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import '../../../shared/StyledInput.css';
import { LOGIN_STATUS } from './loginStatus';
import LoginSwitchTargets, { startLoginSwitch } from '../../Global/LoginSwitcher/LoginSwitchTargets';
import { askConfirm } from '../../../shared/AppDialogs';

// Customer logins (issue #253): the people at a Customer, or its generic
// address, who can sign in to the portal. Staff invite an email already on
// the record (the Customer's own, or a linked Individual's), and switch
// single logins off and on. Shown on any record holding the Customer
// relationship, and on any record that still has logins after it stopped
// being one (they show as paused). A warning triangle in the header flags a
// Customer with no email to invite a login with. The portal address itself
// lives in Platform Settings → Customer Portal, since it's the same for
// every Customer. Backend: customerLoginService.js.
// Admins also get View as Customer (issue #283), which opens the portal as
// one of the logins (picked here when there's more than one) through the
// Login Switcher.

// Re-exported for the Customers list's Logins column.
export { LOGIN_STATUS };

const rowStyle = { position: 'relative', marginBottom: 14, paddingBottom: 12, paddingRight: 28, borderBottom: '1px solid #eef0f3' };
const menuStyle = { position: 'absolute', top: -4, right: -6 };

function StatusPill({ login }) {
  const meta = LOGIN_STATUS[login.status] || LOGIN_STATUS.switched_off;
  return <Pill color={meta.color} background={meta.background} title={meta.title || login.status_reason}>{meta.label}</Pill>;
}

function optionKey(option) {
  return `${option.contact_id ?? ''}|${option.email}`;
}

function CustomerLoginsCard({ entityId, entity, isCustomer }) {
  const { canUpdate } = useScreenAccess();
  const { isAdmin } = usePermissions();
  const navigate = useNavigate();
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [data, setData] = useState(null); // { logins, invite_options, paused_reason }
  const [inviting, setInviting] = useState(false);
  const [picked, setPicked] = useState('');
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');
  const [switchTargets, setSwitchTargets] = useState(null); // the View as Customer picker, when open
  const [switching, setSwitching] = useState(false);
  // Controlled so the card's own menu actions (Invite Login, View as
  // Customer's picker) open it, and the form or notice they show is seen.
  const [open, setOpen] = useState(false);

  // Refetched whenever the record changes, so removing an email or
  // unlinking an Individual shows its login switched off straight away.
  useEffect(() => {
    let cancelled = false;
    axios.get(`/api/commercial-entities/${entityId}/logins`)
      .then((res) => { if (!cancelled) setData(res.data.data); })
      .catch((err) => { if (!cancelled) showError(err, 'Could not load logins'); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, entity]);

  if (!data) return null;
  const { logins, invite_options: inviteOptions, paused_reason: pausedReason, send_environment: sendEnvironment } = data;
  if (!isCustomer && logins.length === 0) return null;

  const run = async (request, fallback) => {
    setSaving(true);
    try {
      const res = await request();
      setData(res.data.data);
      setNotice(res.data.message || '');
      dismissError();
      return true;
    } catch (err) {
      showError(err, fallback);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const startInvite = () => {
    setPicked(inviteOptions.length === 1 ? optionKey(inviteOptions[0]) : '');
    setNotice('');
    setInviting(true);
    setOpen(true);
  };

  const submitInvite = async (e) => {
    e.preventDefault();
    const option = inviteOptions.find((o) => optionKey(o) === picked);
    if (!option) return;
    const ok = await run(
      () => axios.post(`/api/commercial-entities/${entityId}/logins`, { email: option.email, contact_id: option.contact_id }),
      'Invite failed',
    );
    if (ok) setInviting(false);
  };

  const resend = (login) => run(() => axios.post(`/api/commercial-entities/${entityId}/logins/${login.id}/invite`), 'Invite failed');

  const switchOff = async (login) => {
    if (!(await askConfirm(`Switch off ${login.email}? It can't be used to sign in until it's switched back on. The Customer's other logins keep working.`, { confirmLabel: 'Switch Off' }))) return;
    run(() => axios.patch(`/api/commercial-entities/${entityId}/logins/${login.id}/switch-off`), 'Switch off failed');
  };

  const switchOn = (login) => run(() => axios.patch(`/api/commercial-entities/${entityId}/logins/${login.id}/switch-on`), 'Switch on failed');

  const menuFor = (login) => {
    const items = [];
    if (!login.switched_off_reason && !login.has_password && login.status !== 'paused') {
      items.push({ key: 'resend', label: 'Re-send invite', onClick: () => resend(login), disabled: saving });
    }
    if (login.switched_off_reason) {
      items.push({ key: 'switch-on', label: 'Switch on', onClick: () => switchOn(login), disabled: saving });
    } else {
      items.push({ key: 'switch-off', label: 'Switch off', onClick: () => switchOff(login), disabled: saving, danger: true });
    }
    return items;
  };

  const switchTo = async (customerId, loginId) => {
    setSwitching(true);
    try {
      await startLoginSwitch(customerId, loginId);
      setSwitchTargets(null);
      dismissError();
    } catch (err) {
      showError(err, 'Could not start the login switch');
    }
    setSwitching(false);
  };

  // One login (or none) opens straight away; with more, pick which.
  const viewAsCustomer = async () => {
    if (logins.length <= 1) {
      switchTo(entityId, logins[0]?.id ?? null);
      return;
    }
    try {
      const res = await axios.get(`/api/login-switcher/customers/${entityId}`);
      setInviting(false);
      setSwitchTargets(res.data.data);
      setOpen(true);
    } catch (err) {
      showError(err, 'Could not load logins to view as');
    }
  };

  const canInvite = canUpdate && !pausedReason;
  const noEmail = logins.length === 0 && inviteOptions.length === 0;
  const noEmailWarning = noEmail && (
    <span
      title="No email on this Customer. Add an email to this Customer, or link an Individual with an email, to invite a login."
      aria-label="No email on this Customer to invite a login"
      style={{ display: 'flex', color: '#d97706', cursor: 'help' }}
    >
      <AlertTriangle size={16} />
    </span>
  );
  const menuItems = [
    canInvite && {
      key: 'invite-login',
      label: 'Invite Login',
      onClick: startInvite,
      disabled: inviteOptions.length === 0,
      disabledTitle: 'Add an email to this Customer, or link an Individual with an email, to invite a login',
    },
    // With no login yet it opens the portal as the Customer itself, a
    // preview of what they'll see once invited.
    isAdmin && {
      key: 'view-as-customer',
      label: logins.length ? 'View as Customer' : 'Preview as Customer',
      onClick: viewAsCustomer,
      disabled: switching,
    },
  ].filter(Boolean);

  return (
    <Accordion title={`Logins (${logins.length})`} open={open} onToggle={() => setOpen((o) => !o)} headerExtra={noEmailWarning} menuItems={menuItems.length ? menuItems : undefined}>
      {pausedReason && logins.length > 0 && (
        <div style={{ background: '#fff8e1', border: '1px solid #f0d58c', borderRadius: 6, padding: '8px 10px', fontSize: 13, marginBottom: 12 }}>
          Logins are paused: {pausedReason}. They work again with the same passwords once the Customer is back at an open Stage and active.
        </div>
      )}
      {notice && <div style={{ fontSize: 13, color: '#1b5e20', marginBottom: 10 }}>{notice}</div>}
      {switchTargets && (
        <div style={{ ...rowStyle, paddingRight: 0 }}>
          <div style={{ fontSize: 12, color: '#5a6b85', marginBottom: 4 }}>View the portal as</div>
          <LoginSwitchTargets customer={switchTargets} busy={switching} onSwitch={(customer, loginId) => switchTo(customer.id, loginId)} />
          <CancelButton onClick={() => setSwitchTargets(null)} disabled={switching} style={{ marginTop: 8 }} />
        </div>
      )}
      {inviting && (
        <form onSubmit={submitInvite} style={{ ...rowStyle, paddingRight: 0 }}>
          <label style={{ fontSize: 12, color: '#5a6b85', display: 'block', marginBottom: 4 }}>Email to invite</label>
          <select className="styled-input" value={picked} onChange={(e) => setPicked(e.target.value)} style={{ width: '100%' }} autoFocus>
            <option value="">Select...</option>
            {inviteOptions.map((option) => (
              <option key={optionKey(option)} value={optionKey(option)}>
                {option.email} — {option.owner_name} ({option.contact_id == null ? 'Company' : 'Individual'})
              </option>
            ))}
          </select>
          <div style={{ fontSize: 12, color: '#5a6b85', marginTop: 4 }}>
            They'll get an email with a link to set their own password. The link works once and expires in 24 hours.
          </div>
          {sendEnvironment?.nonProduction && (
            sendEnvironment.testEmailAddress ? (
              <div style={{ fontSize: 12, color: '#8a5a00', background: '#fff8e6', border: '1px solid #f0d9a6', borderRadius: 4, padding: '8px 10px', marginTop: 8 }}>
                Non-production: the invite will be delivered to <strong>{sendEnvironment.testEmailAddress}</strong>, whichever email is picked.
              </div>
            ) : (
              <div style={{ fontSize: 12, color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 4, padding: '8px 10px', marginTop: 8 }}>
                Non-production: sending is blocked until a Test Email Address is set on{' '}
                <button type="button" className="link-button" onClick={() => navigate('/global-platform-settings')}>
                  Platform Settings
                </button>.
              </div>
            )
          )}
          <div style={{ marginTop: 12 }}>
            <SaveButton loading={saving} disabled={!picked}>{saving ? 'Sending...' : 'Send invite'}</SaveButton>
            <CancelButton onClick={() => setInviting(false)} disabled={saving} style={{ marginLeft: 8 }} />
          </div>
        </form>
      )}
      {logins.length === 0 && !inviting && (
        <div style={{ fontSize: 13, color: '#999' }}>
          {noEmail ? 'No logins yet. Add an email to this Customer, or link an Individual with an email, to invite one.' : 'No logins yet'}
        </div>
      )}
      {logins.map((login) => (
        <div key={login.id} style={{ ...rowStyle, opacity: login.status === 'switched_off' ? 0.7 : 1 }}>
          {canUpdate && (
            <div style={menuStyle}>
              <RowActionsMenu items={menuFor(login)} ariaLabel={`${login.email} actions`} />
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, fontWeight: 600, wordBreak: 'break-all' }}>{login.email}</span>
            <StatusPill login={login} />
          </div>
          <div style={{ fontSize: 12, color: '#5a6b85', marginTop: 2 }}>
            {login.owner_name} ({login.is_generic ? 'Company' : 'Individual'})
          </div>
          {login.status_reason && (login.status === 'switched_off' || login.status === 'paused') && (
            <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>{login.status_reason}</div>
          )}
        </div>
      ))}
    </Accordion>
  );
}

export default CustomerLoginsCard;
